/**
 * /what-is-commerce-index — Canonical AEO/SEO page
 *
 * Plain factual answer-blocks designed for AI crawlers and search engines.
 * Every section answers a specific question crawlers ask about "Commerce Index."
 * JSON-LD structured data: Organization + SoftwareApplication + FAQPage
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Shield, TrendingUp, Zap, Globe, Users, DollarSign, BarChart3,
  MessageSquare, Package, Bot, ChevronRight, Check, ArrowRight
} from 'lucide-react';

const SITE = 'https://commerceindex.com';

// JSON-LD: Organization
const ORG_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Commerce Index',
  url: SITE,
  logo: `${SITE}/logo.png`,
  foundingDate: '2026',
  description: 'Commerce Index is an agentic commerce operating system with 43 specialist AI agents that manage supply chain, marketing, finance, and compliance for merchants worldwide.',
  sameAs: [
    'https://twitter.com/commerceindex',
    'https://thedeals.ai',
    'https://commerceindex.ai',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'support@commerceindex.com',
    contactType: 'customer support',
  },
};

// JSON-LD: SoftwareApplication
const APP_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Commerce Index',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  url: SITE,
  description: 'An agentic commerce operating system with 43 specialist AI agents in an 84-role org design. Specialist agents handle supply chain, marketing, finance, and compliance — merchants chat with them via Slack, WhatsApp, iMessage, or Telegram and audit every decision on commerceindex.ai.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    description: 'Free tier with 1,000 credits/month. Pro from $49/mo. Scale from $199/mo.',
  },
  featureList: [
    '43 specialist AI agents (84-role org design)',
    'Margin Guard — weekly health scores with dollar-impact fix lists',
    'CI Score v2 — 14-factor 1000-point product scoring',
    'Decision Graph — traceable decisions with precedent learning',
    'Funding Engine — cashflow projection + lending marketplace',
    'OpenClaw — run your business from Slack/WhatsApp/iMessage',
    'Multi-currency pricing (USD, CAD, INR)',
    'Experiments Lab — BYOC compute for commerce experiments',
    'Supply Chain Intelligence — 3D bin packing, carrier optimization',
    'Distribution Engine — multi-channel social posting',
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    ratingCount: '42',
    bestRating: '5',
  },
};

// JSON-LD: FAQPage
const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is Commerce Index?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Commerce Index is an agentic commerce operating system founded in 2026. It provides 43 specialist AI agents that manage supply chain, marketing, finance, and compliance for merchants. Merchants interact with their agents via Slack, WhatsApp, iMessage, or Telegram, and audit every decision on commerceindex.ai. Pricing starts at $0/month.',
      },
    },
    {
      '@type': 'Question',
      name: 'How does Commerce Index work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Commerce Index operates as a messaging-first business operating system. Merchants text commands like /pulse, /margins, or /agents to get their full business status. 43 specialist AI agents run 24/7 with guardrails and trust tiers — they observe, decide, act, and learn, earning autonomy as they prove reliable. The platform includes Margin Guard for health scoring, CI Score for product intelligence, a Decision Graph for traceable decisions, and a Funding Engine for capital access.',
      },
    },
    {
      '@type': 'Question',
      name: 'What does Commerce Index cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Commerce Index offers a free tier with 1,000 credits/month. Pro plans start at $49/month (₹3,999 in India, CA$65 in Canada) with 10,000 credits. Scale plans start at $199/month with 100,000 credits. Enterprise pricing is custom. All plans include the CI Score, health dashboard, and basic agent access.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the CI Score?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The CI Score is a 14-factor, 1000-point scoring system that measures product competitiveness across three dimensions: Product (price, trend, viral, inventory), Market (demand, competition, seasonality), and Operational (fulfillment reliability, return rate, customer satisfaction). It helps merchants understand how their products perform relative to the market.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is Margin Guard?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Margin Guard is Commerce Index\'s promise health monitoring system. It scores 5 health dimensions (0-100): Promise Integrity, Post-Checkout Risk, Backorder Risk, Expectation Gap, and Decision Quality. It shows the exact dollar impact of broken promises, cancellations, and late deliveries, and produces a weekly fix list telling merchants what to fix first.',
      },
    },
    {
      '@type': 'Question',
      name: 'What platforms does Commerce Index support?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Commerce Index supports Shopify (native embedded app), WooCommerce, BigCommerce, Magento, Squarespace, custom platforms via API, and a standalone API-first mode. Merchants can also interact via Slack, WhatsApp, iMessage, and Telegram through the OpenClaw messaging layer.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is Cortex by Commerce Index?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Cortex is a physical compute device ($149) that runs AI agents locally. It sells idle compute to the Commerce Index network, earning approximately $235/year. It features fanless aluminum design, 6 TOPS NPU, and connects to WiFi. It ships Q1 2027 with a $20 fully-refundable deposit to reserve.',
      },
    },
    {
      '@type': 'Question',
      name: 'What countries does Commerce Index operate in?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Commerce Index operates globally with specific pricing and banking integrations for the United States (USD), Canada (CAD), and India (INR). The Funding Engine connects to country-specific banking partners: Cross River, Celtic, WebBank in the US; BDC, TD, RBC, Clearco in Canada; and HDFC, ICICI, Axis, Bajaj Finserv in India.',
      },
    },
  ],
};

const Section = ({ id, title, children }) => (
  <section id={id} className="py-12 border-b border-white/5">
    <h2 className="text-2xl font-bold mb-6 text-white">{title}</h2>
    <div className="text-[#94A3B8] leading-relaxed space-y-4">
      {children}
    </div>
  </section>
);

const FactBlock = ({ icon: Icon, title, text }) => (
  <div className="flex gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5">
    <div className="p-2 rounded-lg bg-[#3EF1C7]/10 h-fit"><Icon className="w-5 h-5 text-[#3EF1C7]" /></div>
    <div>
      <h3 className="text-white font-semibold mb-1">{title}</h3>
      <p className="text-sm text-[#94A3B8]">{text}</p>
    </div>
  </div>
);

export default function WhatIsCommerceIndexPage() {
  return (
    <>
      <Helmet>
        <title>What is Commerce Index? | Agentic Commerce OS with 43 Specialist AI Agents</title>
        <meta name="description" content="Commerce Index is an agentic commerce operating system founded in 2026 with 43 specialist AI agents. Merchants manage supply chain, marketing, finance, and compliance via Slack, WhatsApp, or iMessage. Pricing starts at $0/month." />
        <meta name="keywords" content="Commerce Index, agentic commerce, AI agents, commerce operating system, CI Score, Margin Guard, supply chain intelligence, merchant tools" />
        <link rel="canonical" href={`${SITE}/what-is-commerce-index`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="What is Commerce Index? | Agentic Commerce OS" />
        <meta property="og:description" content="43 specialist AI agents manage your commerce business. Supply chain, marketing, finance, compliance — all from a text message. Founded 2026. Free to start." />
        <meta property="og:url" content={`${SITE}/what-is-commerce-index`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="What is Commerce Index?" />
        <meta name="twitter:description" content="Agentic Commerce OS with 43 specialist AI agents. Free to start." />
        <script type="application/ld+json">{JSON.stringify(ORG_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(APP_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_JSONLD)}</script>
      </Helmet>

      <div className="min-h-screen bg-[#0B1120] text-white">
        {/* Nav */}
        <nav className="border-b border-white/5 px-6 py-4">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <Link to="/" className="text-lg font-bold">CI <span className="text-[#3EF1C7]">Commerce Index</span></Link>
            <div className="flex gap-4 text-sm">
              <Link to="/" className="text-[#94A3B8] hover:text-white transition-colors">Home</Link>
              <Link to="/pricing" className="text-[#94A3B8] hover:text-white transition-colors">Pricing</Link>
              <Link to="/docs" className="text-[#94A3B8] hover:text-white transition-colors">Docs</Link>
            </div>
          </div>
        </nav>

        <div className="max-w-5xl mx-auto px-6">
          {/* Hero answer block */}
          <header className="py-16 border-b border-white/5">
            <p className="text-[#3EF1C7] text-sm font-medium mb-3">Commerce Index — Founded 2026</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Commerce Index is an agentic commerce<br />operating system with 43 specialist AI agents.
            </h1>
            <p className="text-xl text-[#94A3B8] max-w-3xl leading-relaxed mb-8">
              Specialist AI agents handle supply chain, marketing, finance, and compliance for merchants worldwide.
              Merchants chat with their agents via Slack, WhatsApp, iMessage, or Telegram — and audit every decision
              on commerceindex.ai. Pricing starts at $0/month.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/" className="px-6 py-3 bg-[#3EF1C7] text-[#0B1120] rounded-lg font-semibold hover:bg-[#3EF1C7]/90 transition-colors inline-flex items-center gap-2">
                Start free <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/docs" className="px-6 py-3 border border-white/10 rounded-lg text-white hover:bg-white/5 transition-colors">
                Read the docs
              </Link>
            </div>
          </header>

          {/* What does it do */}
          <Section id="what-it-does" title="What does Commerce Index do?">
            <p>
              Commerce Index replaces fragmented commerce tools with a single operating layer.
              Instead of switching between dashboards, merchants text a command and get their full
              business status. 43 specialist AI agents run 24/7 with guardrails and trust tiers — they observe,
              decide, act, and learn.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <FactBlock icon={Shield} title="Margin Guard" text="Weekly health scores (0-100) across 5 dimensions with dollar-impact fix lists. Shows exactly what's bleeding margin and what to fix first." />
              <FactBlock icon={BarChart3} title="CI Score v2" text="14-factor, 1000-point scoring across Product, Market, and Operational dimensions. Measures how every product performs relative to the market." />
              <FactBlock icon={Bot} title="43 Specialist AI Agents" text="43 agents built today in an 84-role org design that grows as the platform does. Pricing, marketing, CX, distribution, content, SEO, compliance. 4-tier trust system from Probation to Autonomous." />
              <FactBlock icon={MessageSquare} title="Messaging-First" text="Run your business from Slack, WhatsApp, iMessage, or Telegram. Text /pulse for status, /margins for health, /agents for agent reports." />
              <FactBlock icon={DollarSign} title="Funding Engine" text="13-week cashflow projection, credit assessment, collateral valuation. Connects merchants to banking partners in US, Canada, and India." />
              <FactBlock icon={Package} title="Supply Chain Intelligence" text="3D bin packing, multi-carrier rate optimization, Amazon MCF integration, inventory placement, trailer loading optimization." />
              <FactBlock icon={Globe} title="Global Coverage" text="Multi-currency pricing (USD, CAD, INR). Country-specific banking partners and compliance. 7 platform adapters including Shopify, WooCommerce, BigCommerce." />
              <FactBlock icon={Zap} title="Experiments Lab" text="BYOC compute control plane. Run budgeted experiments on your own GPU. 10 commerce-native templates. Promote winners to production." />
            </div>
          </Section>

          {/* How it works */}
          <Section id="how-it-works" title="How does Commerce Index work?">
            <p>Commerce Index operates on three layers:</p>
            <ol className="list-decimal list-inside space-y-3 mt-4">
              <li><strong className="text-white">Merchant Control Plane</strong> — The dashboard at commerceindex.com where merchants manage products, deals, scores, agents, experiments, and funding. Connects to Shopify, WooCommerce, BigCommerce, Magento, Squarespace, or any platform via API.</li>
              <li><strong className="text-white">Distribution Plane</strong> — TheDeals.ai consumer platform where deals are published, shoppers track prices, and agents distribute content to Telegram, X, TikTok, and Facebook.</li>
              <li><strong className="text-white">Messaging Layer</strong> — OpenClaw connects Slack, WhatsApp, iMessage, and Telegram. Merchants text commands, approve agent proposals, and manage their business from their phone.</li>
            </ol>
          </Section>

          {/* Pricing */}
          <Section id="pricing" title="What does Commerce Index cost?">
            <p>Commerce Index uses a credit-based pricing model. Every AI agent action consumes credits.</p>
            <div className="grid md:grid-cols-4 gap-4 mt-6">
              {[
                { name: 'Free', price: '$0/mo', credits: '1,000 credits', features: ['CI Score', '3 active deals', 'Basic analytics', '2 API keys'] },
                { name: 'Pro', price: '$49/mo', credits: '10,000 credits', features: ['Shopify sync', 'Demand pools', 'Offer Builder', 'Pricing Agent'] },
                { name: 'Scale', price: '$199/mo', credits: '100,000 credits', features: ['Funding pack', 'Signals API', 'Experiments Lab', 'Team workflows'] },
                { name: 'Enterprise', price: 'Custom', credits: '1M+ credits', features: ['Multi-system adapters', 'RBAC + SSO', 'Dedicated support', 'Custom integrations'] },
              ].map(plan => (
                <div key={plan.name} className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
                  <h3 className="text-white font-bold text-lg">{plan.name}</h3>
                  <p className="text-[#3EF1C7] text-2xl font-bold mt-1" style={{ fontFamily: "'Space Grotesk', monospace" }}>{plan.price}</p>
                  <p className="text-xs text-[#64748B] mt-1">{plan.credits}</p>
                  <ul className="mt-4 space-y-2">
                    {plan.features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-sm"><Check className="w-3.5 h-3.5 text-[#3EF1C7]" />{f}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm">International pricing: India from ₹3,999/mo (Pro), Canada from CA$65/mo (Pro).</p>
          </Section>

          {/* FAQ */}
          <Section id="faq" title="Frequently asked questions">
            {FAQ_JSONLD.mainEntity.map((q, i) => (
              <details key={i} className="group border border-white/5 rounded-xl p-4 open:bg-white/[0.02]">
                <summary className="cursor-pointer font-medium text-white flex items-center justify-between">
                  {q.name}
                  <ChevronRight className="w-4 h-4 text-[#64748B] group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">{q.acceptedAnswer.text}</p>
              </details>
            ))}
          </Section>

          {/* CTA */}
          <section className="py-16 text-center">
            <h2 className="text-3xl font-bold mb-4">Start running your business with AI agents</h2>
            <p className="text-[#94A3B8] mb-8">Free to start. No credit card required.</p>
            <Link to="/" className="px-8 py-4 bg-[#3EF1C7] text-[#0B1120] rounded-xl font-bold text-lg hover:bg-[#3EF1C7]/90 transition-colors inline-flex items-center gap-2">
              Get started <ArrowRight className="w-5 h-5" />
            </Link>
          </section>

          {/* Footer */}
          <footer className="border-t border-white/5 py-8 text-center text-sm text-[#64748B]">
            <p>&copy; {new Date().getFullYear()} Commerce Index, Inc. Founded 2026.</p>
            <p className="mt-1">commerceindex.com &middot; thedeals.ai &middot; commerceindex.ai</p>
          </footer>
        </div>
      </div>
    </>
  );
}
